/**
 * Report Layout Migration Utility (Story 5.32, ADR-2)
 *
 * Shared between web and server — single source of truth for
 * migrating flat widget layouts to multi-page format.
 *
 * crypto.randomUUID() works in both browser and Cloudflare Workers.
 */

import type { ReportPage, ReportPageWidget } from "./report-rendering";

/**
 * Migrates any layout_json shape to the multi-page `ReportPage[]` format.
 *
 * Key behaviors:
 * - `null`, `undefined`, `{}` → default single empty page
 * - `{ widgets: [] }` → single page with empty widgets (valid)
 * - `{ pages: [] }` → default single empty page (enforces min-1)
 * - `{ widgets: [...], pages: [...] }` → uses `pages` (precedence rule)
 * - Calling twice on migrated data → returns as-is (idempotent)
 *
 * @param layout - Raw layout_json value (parsed or unparsed)
 * @param defaultTitle - Title for the auto-created page (default: "Page 1")
 * @returns Normalized `ReportPage[]` with at least one page
 */
export function migrateLayoutToPages(
  layout: unknown,
  defaultTitle?: string
): ReportPage[] {
  if (!layout || typeof layout !== "object") {
    return [makeDefaultPage(defaultTitle)];
  }

  const obj = layout as Record<string, unknown>;

  // Pages format takes precedence (even if widgets key also exists).
  // Sanitize each page defensively (Story 37.1): persisted DB JSON is now fed
  // through here for the first time, so a malformed page (non-array `widgets`,
  // garbage widget elements, missing `order`, non-string `id`/`title`,
  // duplicate ids) must not crash downstream callers that do
  // `page.widgets.map(...)`, read `widget.position.col`, or use `page.id` as a
  // React key / lookup key.
  if ("pages" in obj && Array.isArray(obj.pages) && obj.pages.length > 0) {
    return dedupePageIds(obj.pages.map((p, i) => sanitizePage(p, i, defaultTitle)));
  }

  // Flat widgets format → wrap in single page
  if ("widgets" in obj && Array.isArray(obj.widgets)) {
    return [
      {
        id: safeRandomUUID(),
        title: defaultTitle ?? "Page 1",
        order: 0,
        widgets: sanitizeWidgets(obj.widgets),
      },
    ];
  }

  // Fallback: empty single page
  return [makeDefaultPage(defaultTitle)];
}

/**
 * `crypto.randomUUID()` is only defined in secure contexts and throws in some
 * runtimes (plain HTTP, certain embedded/test environments). These helpers run
 * on untrusted persisted data whose whole point is "never crash", so fall back
 * to a non-cryptographic id when the native API is unavailable (Story 37.1).
 */
function safeRandomUUID(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
      // fall through to the deterministic-shape fallback below
    }
  }
  // RFC-4122-shaped fallback (NOT cryptographically strong — ids only need to be
  // collision-resistant within one report, which this is for editor purposes).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Keep only structurally-valid widgets, coercing recoverable fields. A persisted
 * `widgets` array can contain `null`, primitives, or objects with a broken shape
 * — any of which crash the grid layout (`widget.position.col`) or widget config
 * readers (`config.metricKey`). Two tiers (Story 37.1):
 *
 * - DROP the widget when its identity/position is unusable: missing `id`/`type`,
 *   or a `position` whose `row`/`col`/`colSpan` is not a FINITE, NON-NEGATIVE
 *   number. (A `NaN`/negative position can't be repaired meaningfully, and if it
 *   survived it would render broken and then serialize back as `null` on the
 *   next autosave — silently dropping the widget a round-trip later, the exact
 *   data-loss class this story prevents.)
 * - BACKFILL recoverable fields rather than drop, so a real widget is never lost
 *   over a missing `title`/`config`: `title` → "", `config` → {}.
 */
function isFiniteNonNegative(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}

function sanitizeWidgets(raw: unknown): ReportPageWidget[] {
  if (!Array.isArray(raw)) return [];
  const out: ReportPageWidget[] = [];
  for (const w of raw) {
    if (!w || typeof w !== "object") continue;
    const widget = w as Record<string, unknown>;
    const pos = widget.position as Record<string, unknown> | undefined;
    if (typeof widget.id !== "string" || !widget.id) continue;
    if (typeof widget.type !== "string" || !widget.type) continue;
    if (
      !pos ||
      typeof pos !== "object" ||
      !isFiniteNonNegative(pos.row) ||
      !isFiniteNonNegative(pos.col) ||
      !isFiniteNonNegative(pos.colSpan)
    ) {
      continue;
    }
    out.push({
      id: widget.id,
      type: widget.type,
      title: typeof widget.title === "string" ? widget.title : "",
      position: {
        row: pos.row,
        col: pos.col,
        colSpan: pos.colSpan,
        ...(isFiniteNonNegative(pos.rowSpan) ? { rowSpan: pos.rowSpan } : {}),
      },
      config:
        widget.config && typeof widget.config === "object"
          ? (widget.config as Record<string, unknown>)
          : {},
      ...(typeof widget.autoSize === "boolean" ? { autoSize: widget.autoSize } : {}),
    });
  }
  return out;
}

/**
 * Coerce an untrusted persisted page object into a structurally-valid
 * `ReportPage`. Valid pages pass through with their fields intact; malformed
 * ones get safe fallbacks so callers can always rely on `id: string`,
 * `title: string`, `order: number`, and `widgets: array` of valid widgets
 * (Story 37.1). An empty-string `category` is treated as absent so the page
 * falls back to the default section instead of becoming sidebar-invisible.
 */
function sanitizePage(
  raw: unknown,
  index: number,
  defaultTitle?: string
): ReportPage {
  if (!raw || typeof raw !== "object") {
    return { ...makeDefaultPage(defaultTitle), order: index };
  }
  const p = raw as Record<string, unknown>;
  return {
    id: typeof p.id === "string" && p.id ? p.id : safeRandomUUID(),
    title:
      typeof p.title === "string" && p.title
        ? p.title
        : defaultTitle ?? `Page ${index + 1}`,
    order: typeof p.order === "number" && Number.isFinite(p.order) ? p.order : index,
    ...(typeof p.category === "string" && p.category ? { category: p.category } : {}),
    widgets: sanitizeWidgets(p.widgets),
  };
}

/**
 * Ensure page ids are unique — duplicate ids from persisted JSON cause React key
 * collisions and ambiguous active-page lookups. Later duplicates get a fresh id.
 */
function dedupePageIds(pages: ReportPage[]): ReportPage[] {
  const seen = new Set<string>();
  return pages.map((p) => {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      return p;
    }
    let id = safeRandomUUID();
    while (seen.has(id)) id = safeRandomUUID();
    seen.add(id);
    return { ...p, id };
  });
}

function makeDefaultPage(title?: string): ReportPage {
  return {
    id: safeRandomUUID(),
    title: title ?? "Page 1",
    order: 0,
    widgets: [],
  };
}

/**
 * Defensively parse a persisted `layoutJson` string (Story 37.1).
 *
 * The server returns `layoutJson` as a RAW JSON string (unlike the template
 * path, which hands the editor a pre-parsed object). A corrupt or truncated
 * blob must NOT crash the editor or wipe sibling state (`sectionLabels`,
 * `activeSections`, `sectionOrder`). This wrapper:
 *
 * - returns the parsed object on success;
 * - returns `null` on ANY failure (invalid JSON, or JSON that isn't an object —
 *   e.g. `"42"`, `"null"`, `"[1,2]"`), optionally invoking `onError` so the
 *   caller can log/telemeter.
 *
 * `null` flows safely through `migrateLayoutToPages` (→ default single page) and
 * every `extract*` helper (→ empty defaults), which already guard non-object
 * input. The corruption contract (AC #4) is satisfied by returning `null` here
 * rather than throwing.
 *
 * @param raw - The raw `layoutJson` string (or already-parsed/`null` value).
 * @param onError - Optional callback invoked with the caught error on parse
 *                  failure (e.g. `console.warn`). Never rethrows.
 * @returns The parsed layout object, or `null` when unparseable / not an object.
 */
export function safeParseLayout(
  raw: unknown,
  onError?: (err: unknown) => void
): Record<string, unknown> | null {
  // Already an object (template path / pre-parsed) — pass through unchanged.
  // Reject arrays here too, symmetric with the string branch below (an array is
  // not a valid report-layout blob), so both entry points behave identically.
  if (raw && typeof raw === "object") {
    return Array.isArray(raw) ? null : (raw as Record<string, unknown>);
  }
  if (typeof raw !== "string") {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    // Only object layouts are usable; primitives/arrays degrade to null so the
    // downstream defaults kick in (an array layout is not a valid report blob).
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch (err) {
    onError?.(err);
    return null;
  }
}

/**
 * Flattens all widgets across all pages into a single array.
 * Useful for functions that need to operate on all widgets regardless of page.
 */
export function flattenPagesWidgets(pages: ReportPage[]): ReportPageWidget[] {
  return pages.flatMap((p) => p.widgets);
}
